import React, { useState, useRef, useEffect, useCallback } from 'react'
import axios from 'axios'
import { timeAgo } from './UnclaimedWorkList'

/* ---- Source labels ---- */
const SOURCE_LABELS = {
  voicemail: 'Voicemail',
  sms: 'SMS',
  phone_call: 'Phone Call',
  email: 'Email',
  field_observation: 'Field Observation',
  walk_in: 'Walk-in',
  aspire_work_order: 'Aspire WO',
  customer_portal: 'Customer Portal',
}

/* ---- Small SVG icons (inline, matching mockup) ---- */
const IconVoicemail = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="5.5" cy="11.5" r="4.5"/><circle cx="18.5" cy="11.5" r="4.5"/><line x1="5.5" y1="16" x2="18.5" y2="16"/>
  </svg>
)
const IconSms = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const IconPhone = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"/>
  </svg>
)
const IconEmail = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
)

const SOURCE_ICONS = {
  voicemail: IconVoicemail,
  sms: IconSms,
  phone_call: IconPhone,
  email: IconEmail,
  field_observation: IconPhone,
  walk_in: IconPhone,
  aspire_work_order: IconPhone,
  customer_portal: IconPhone,
}

/* ---- Action SVGs ---- */
const IconClaim = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
)
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
)
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IconTrash = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
)
const IconFile = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
)
const IconInbox = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
  </svg>
)

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / 1048576).toFixed(1) + ' MB'
}

function formatDueDate(dateStr) {
  if (!dateStr) return 'None'
  const due = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  const diffDays = Math.round((dueDay - today) / 86400000)
  if (diffDays < 0) return 'Overdue'
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function UnclaimedWorkDetail({ item, url, currentUserId, onUpdate }) {
  const [assignSearch, setAssignSearch] = useState('')
  const [assignResults, setAssignResults] = useState([])
  const [showAssignDropdown, setShowAssignDropdown] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [aspireId, setAspireId] = useState('')
  const [loading, setLoading] = useState(false)
  const searchTimer = useRef(null)

  // Reset local state when item changes
  useEffect(() => {
    setAssignSearch('')
    setAssignResults([])
    setShowAssignDropdown(false)
    setSelectedEmployee(null)
    setAspireId(item?.aspire_work_ticket_id || '')
  }, [item?._id])

  // Employee search
  useEffect(() => {
    if (!url || assignSearch.length < 2) {
      setAssignResults([])
      return
    }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      axios.get(`${url}/api/hmi/unclaimed-work/employees`, { params: { q: assignSearch } })
        .then(res => {
          if (res.data?.success) {
            setAssignResults(res.data.data)
            setShowAssignDropdown(true)
          }
        })
        .catch(() => {})
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [assignSearch, url])

  const patchItem = useCallback(async (updates) => {
    if (!item || !url) return
    setLoading(true)
    try {
      await axios.patch(`${url}/api/hmi/unclaimed-work/${item._id}`, updates)
      if (onUpdate) onUpdate()
    } catch (err) {
      console.error('[UW_DETAIL] Patch failed:', err)
    }
    setLoading(false)
  }, [item, url, onUpdate])

  const handleAssign = useCallback(() => {
    if (!selectedEmployee) return
    patchItem({
      state: 'assigned',
      assigned_to_employee: selectedEmployee._id,
      assigned_to_name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim(),
    })
  }, [selectedEmployee, patchItem])

  const handleClaim = useCallback(() => {
    patchItem({
      state: 'assigned',
      assigned_to: currentUserId,
    })
  }, [currentUserId, patchItem])

  const handleComplete = useCallback(() => {
    patchItem({ state: 'completed' })
  }, [patchItem])

  const handleLinkAspire = useCallback(() => {
    if (!aspireId.trim()) return
    patchItem({ aspire_work_ticket_id: aspireId.trim() })
  }, [aspireId, patchItem])

  const handleArchive = useCallback(() => {
    patchItem({ state: 'archived' })
  }, [patchItem])

  /* ---- Empty state ---- */
  if (!item) {
    return (
      <div className="uw-detail-panel">
        <div className="uw-empty-state">
          <div className="uw-empty-glyph">
            <IconInbox />
          </div>
          <div className="uw-empty-title">Select an item</div>
          <div className="uw-empty-desc">
            Choose an item from the list to view details and take action.
          </div>
        </div>
      </div>
    )
  }

  /* ---- Detail view ---- */
  const sourceKey = item.source || 'phone_call'
  const SourceBadgeIcon = SOURCE_ICONS[sourceKey] || SOURCE_ICONS.phone_call
  const serviceTypes = item.service_types || []
  const attachments = item.attachments || []
  const isDueOverdue = item.due_date && new Date(item.due_date) < new Date()
  const createdByName = item.created_by
    ? `${item.created_by.firstName || ''} ${item.created_by.lastName || ''}`.trim()
    : 'System'

  return (
    <div className="uw-detail-panel">
      {/* Header */}
      <div className="uw-detail-head">
        <div className="uw-detail-title">
          {item.title || item.body?.substring(0, 80) || 'Untitled'}
        </div>
        <div className="uw-detail-meta-row">
          <span className={`uw-detail-source-badge ${sourceKey}`}>
            <SourceBadgeIcon />
            {SOURCE_LABELS[sourceKey] || sourceKey}
          </span>
          <span className={`uw-pill uw-pill-${item.priority || 'normal'}`}>
            {(item.priority || 'normal').charAt(0).toUpperCase() + (item.priority || 'normal').slice(1)}
          </span>
          <span className="uw-detail-time">
            {timeAgo(item.createdAt)} ago &middot; Created by {createdByName}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="uw-detail-body">
        {/* Transcript */}
        {item.body && (
          <div className="uw-transcript-block">
            <p>{item.body}</p>
          </div>
        )}

        {/* Info grid */}
        <div className="uw-info-grid">
          <div>
            <div className="uw-info-label">Contact</div>
            <div className="uw-info-value">
              {item.contact_name
                ? <span className="contact-link">{item.contact_name}</span>
                : <span style={{ color: 'var(--ink-4)' }}>Not set</span>
              }
            </div>
          </div>
          <div>
            <div className="uw-info-label">Service Type</div>
            <div className="uw-info-value">
              {serviceTypes.length > 0 ? serviceTypes.join(', ') : 'Not set'}
            </div>
          </div>
          <div>
            <div className="uw-info-label">Due Date</div>
            <div className="uw-info-value" style={isDueOverdue ? { color: 'var(--risk)', fontWeight: 600 } : undefined}>
              {formatDueDate(item.due_date)}
            </div>
          </div>
          <div>
            <div className="uw-info-label">Priority</div>
            <div className="uw-info-value">
              <span className={`uw-pill uw-pill-${item.priority || 'normal'}`} style={{ fontSize: 11 }}>
                {(item.priority || 'normal').charAt(0).toUpperCase() + (item.priority || 'normal').slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Assigned to (if assigned) */}
        {item.assigned_to_name && (
          <div style={{ marginBottom: 16 }}>
            <div className="uw-info-label" style={{ marginBottom: 6 }}>Assigned To</div>
            <div className="uw-info-value" style={{ fontWeight: 600 }}>
              {item.assigned_to_name}
            </div>
          </div>
        )}

        {/* Aspire link (if set) */}
        {item.aspire_work_ticket_id && (
          <div style={{ marginBottom: 16 }}>
            <div className="uw-info-label" style={{ marginBottom: 6 }}>Aspire Work Ticket</div>
            <div className="uw-info-value" style={{ fontFamily: 'var(--f-mono)', fontSize: 12 }}>
              {item.aspire_work_ticket_id}
            </div>
          </div>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div className="uw-info-label" style={{ marginBottom: 6 }}>Attachments</div>
            <div className="uw-attachments">
              {attachments.map((att, i) => (
                <a
                  key={i}
                  className="uw-att-chip"
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconFile />
                  {att.filename || 'file'}
                  {att.size && <span className="att-size">{formatSize(att.size)}</span>}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action section */}
      {item.state !== 'completed' && item.state !== 'archived' && (
        <div className="uw-action-section">
          <div className="uw-action-section-title">Actions</div>

          {/* Assign row */}
          <div className="uw-assign-row">
            <input
              className="uw-assign-input"
              placeholder="Search team member to assign..."
              value={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : assignSearch}
              onChange={e => {
                if (selectedEmployee) setSelectedEmployee(null)
                setAssignSearch(e.target.value)
              }}
              onFocus={() => assignResults.length > 0 && setShowAssignDropdown(true)}
              onBlur={() => setTimeout(() => setShowAssignDropdown(false), 200)}
            />
            <button
              className="uw-btn uw-btn-outline uw-btn-sm"
              onClick={handleAssign}
              disabled={!selectedEmployee || loading}
            >
              Assign
            </button>

            {showAssignDropdown && assignResults.length > 0 && (
              <div className="uw-employee-dropdown">
                {assignResults.map(emp => (
                  <div
                    key={emp._id}
                    className="uw-employee-option"
                    onMouseDown={() => {
                      setSelectedEmployee(emp)
                      setAssignSearch('')
                      setShowAssignDropdown(false)
                    }}
                  >
                    <span className="uw-employee-name">
                      {emp.firstName} {emp.lastName}
                    </span>
                    {emp.title && <span className="uw-employee-role">{emp.title}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="uw-action-row">
            <button
              className="uw-btn uw-btn-accent"
              style={{ flex: 1 }}
              onClick={handleClaim}
              disabled={loading}
            >
              <IconClaim /> Claim
            </button>
            <button
              className="uw-btn uw-btn-outline"
              style={{ flex: 1 }}
              disabled
              title="Coming soon"
            >
              <IconEdit /> Edit
            </button>
            <button
              className="uw-btn uw-btn-ok"
              style={{ flex: 1 }}
              onClick={handleComplete}
              disabled={loading}
            >
              <IconCheck /> Complete
            </button>
          </div>

          {/* Aspire link row */}
          <div className="uw-aspire-row">
            <input
              className="uw-aspire-input"
              placeholder="Aspire Work Ticket ID"
              value={aspireId}
              onChange={e => setAspireId(e.target.value)}
            />
            <button
              className="uw-btn uw-btn-ghost uw-btn-sm"
              onClick={handleLinkAspire}
              disabled={!aspireId.trim() || loading}
            >
              Link
            </button>
          </div>

          {/* Archive */}
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button
              className="uw-btn uw-btn-ghost uw-btn-sm"
              style={{ color: 'var(--ink-4)' }}
              onClick={handleArchive}
              disabled={loading}
            >
              <IconTrash /> Archive
            </button>
          </div>
        </div>
      )}

      {/* Completed state actions */}
      {item.state === 'completed' && (
        <div className="uw-action-section">
          <div className="uw-action-section-title">Completed</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 8 }}>
            This item has been marked as completed.
          </div>
          {item.aspire_work_ticket_id && (
            <div style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}>
              Aspire WT: {item.aspire_work_ticket_id}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
